/**
 * View Switcher Component
 *
 * Dropdown that switches between:
 * 1. Internal personas (Financial Controller, Analyst, CEO, CFO)
 * 2. Partner views (Bank - KeyBank, Logistics - ShipBob)
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import {
  DollarSign,
  BarChart3,
  TrendingUp,
  Building2,
  Truck,
  ChevronDown,
  User,
  Shield,
  ShieldCheck,
  Landmark,
  Car,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGlobalTranslation } from '@/components/GlobalTranslationProvider';
import { useCentralizedPersona } from '@/hooks/useCentralizedPersona';

interface ViewOption {
  id: string;
  label: string;
  description?: string;
  icon: React.ElementType;
  color: string;
  route?: string;
  type: 'persona' | 'partner';
  disabled?: boolean;
}

const internalPersonas: ViewOption[] = [
  {
    id: 'controller',
    label: 'Financial Controller',
    description: 'Cash flow & payments',
    icon: DollarSign,
    color: 'blue',
    type: 'persona',
  },
  {
    id: 'analyst',
    label: 'Business Analyst',
    description: 'Data & insights',
    icon: BarChart3,
    color: 'purple',
    type: 'persona',
  },
  {
    id: 'ceo',
    label: 'Chief Executive',
    description: 'Strategic overview',
    icon: TrendingUp,
    color: 'green',
    type: 'persona',
  },
  {
    id: 'cfo',
    label: 'Chief Financial Officer',
    description: 'Financial strategy',
    icon: User,
    color: 'indigo',
    type: 'persona',
  },
];

const partnerViews: ViewOption[] = [
  {
    id: 'bank',
    label: 'Capital Partner View',
    description: 'Portfolio & verification',
    icon: Building2,
    color: 'slate',
    route: '/bank/dashboard',
    type: 'partner',
  },
  {
    id: 'capital-mx',
    label: 'Socio de Capital — México',
    description: 'Factoraje NAFIN y comité de crédito',
    icon: Landmark,
    color: 'green',
    route: '/capital-mx',
    type: 'partner',
  },
  {
    id: 'tremec-demo',
    label: 'TREMEC Compliance',
    description: 'Supplier compliance center',
    icon: ShieldCheck,
    color: 'green',
    route: '/tremec-demo',
    type: 'partner',
  },
  {
    id: 'secretary-economy',
    label: 'Secretary of Economy',
    description: 'Capital stack analysis',
    icon: Landmark,
    color: 'blue',
    route: '/secretary-economy',
    type: 'partner',
  },
  {
    id: 'katcon-portal',
    label: 'Katcon Mexico',
    description: 'Supplier compliance',
    icon: Shield,
    color: 'green',
    route: '/katcon-portal',
    type: 'partner',
  },
  {
    id: 'nexteer-portal',
    label: 'Nexteer Automotive',
    description: 'ICTS & USMCA compliance',
    icon: Shield,
    color: 'indigo',
    route: '/nexteer-portal',
    type: 'partner',
  },
  {
    id: 'toyota-compliance',
    label: 'Toyota Compliance',
    description: 'Supply chain risk center',
    icon: Car,
    color: 'green',
    route: '/toyota-compliance',
    type: 'partner',
  },
  {
    id: 'gdls-compliance',
    label: 'GDLS Compliance',
    description: 'Defense SCR & ITAR',
    icon: Shield,
    color: 'slate',
    route: '/gdls-compliance',
    type: 'partner',
  },
  {
    id: 'ford-compliance',
    label: 'Ford North America',
    description: 'Traceability & rules of origin',
    icon: Car,
    color: 'blue',
    route: '/ford-compliance',
    type: 'partner',
  },
  {
    id: 'mercedes-compliance',
    label: 'Mercedes-Benz R&D NA',
    description: 'Cross-document traceability',
    icon: Car,
    color: 'slate',
    route: '/mercedes-compliance',
    type: 'partner',
  },
  {
    id: 'logistics',
    label: 'Logistics Partner View',
    description: 'Coming soon',
    icon: Truck,
    color: 'orange',
    route: '/logistics/dashboard',
    type: 'partner',
    disabled: true,
  },
];

export function ViewSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { persona, displayName, setCurrentPersonaKey } = useCentralizedPersona();
  const { language } = useGlobalTranslation();
  const es = language === 'es';
  const personaLabelEs: Record<string, string> = {
    controller: 'Controlador Financiero',
    analyst: 'Analista de Negocios',
    ceo: 'Director Ejecutivo',
    cfo: 'Director Financiero',
  };
  const personaDescEs: Record<string, string> = {
    controller: 'Flujo de efectivo y pagos',
    analyst: 'Datos e insights',
    ceo: 'Visión estratégica',
    cfo: 'Finanzas ejecutivas',
  };
  const viewLabel = (v: ViewOption) =>
    es && v.type === 'persona' && personaLabelEs[v.id] ? personaLabelEs[v.id] : v.label;
  const viewDesc = (v: ViewOption) =>
    es && v.type === 'persona' && personaDescEs[v.id] ? personaDescEs[v.id] : v.description;

  // Determine current view based on route
  // FR-93: `/capital-mx` matches by prefix, like `/bank`, so the expediente
  // detail (`/capital-mx/expediente/:id`) resolves to the capital-partner view
  // instead of falling through to an internal persona. Exact-match here was why
  // the switcher showed the wrong current view on that sub-route.
  const isPartnerView = location.pathname.startsWith('/bank') || location.pathname.startsWith('/logistics') ||
                        location.pathname.startsWith('/capital-mx') ||
                        location.pathname === '/tremec-compliance' || location.pathname === '/secretary-economy' ||
                        location.pathname === '/katcon-portal' || location.pathname === '/nexteer-portal' ||
                        location.pathname === '/toyota-compliance' || location.pathname === '/gdls-compliance';
  const currentPartnerView = location.pathname.startsWith('/capital-mx') ? 'capital-mx' :
                             location.pathname.startsWith('/bank') ? 'bank' :
                             location.pathname.startsWith('/logistics') ? 'logistics' :
                             location.pathname === '/tremec-compliance' ? 'tremec-compliance' :
                             location.pathname === '/secretary-economy' ? 'secretary-economy' :
                             location.pathname === '/katcon-portal' ? 'katcon-portal' :
                             location.pathname === '/nexteer-portal' ? 'nexteer-portal' :
                             location.pathname === '/toyota-compliance' ? 'toyota-compliance' :
                             location.pathname === '/gdls-compliance' ? 'gdls-compliance' : null;

  // Get current view option
  const getCurrentView = (): ViewOption => {
    if (currentPartnerView) {
      return partnerViews.find(v => v.id === currentPartnerView) || partnerViews[0];
    }
    return internalPersonas.find(p => p.id === persona) || internalPersonas[0];
  };

  const currentView = getCurrentView();
  const Icon = currentView.icon;

  const handleSelectPersona = (personaId: string) => {
    setCurrentPersonaKey(personaId);
    // If we're on a partner route, navigate back to dashboard
    if (isPartnerView) {
      navigate('/dashboard');
    }
  };

  const handleSelectPartner = (view: ViewOption) => {
    if (view.disabled || !view.route) return;
    navigate(view.route);
  };

  const getColorClasses = (color: string, isActive: boolean) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      blue: { bg: 'bg-accent', text: 'text-primary', border: 'border-primary/30' },
      purple: { bg: 'bg-accent', text: 'text-accent-foreground', border: 'border-border' },
      green: { bg: 'bg-success-muted', text: 'text-success', border: 'border-success/30' },
      indigo: { bg: 'bg-accent', text: 'text-primary', border: 'border-primary/30' },
      slate: { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border' },
      orange: { bg: 'bg-warning-muted', text: 'text-warning', border: 'border-warning/30' },
    };
    return colors[color] || colors.blue;
  };

  const colorClasses = getColorClasses(currentView.color, true);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors hover:bg-accent',
            colorClasses.bg,
            colorClasses.text,
            colorClasses.border
          )}
        >
          <Icon className="h-4 w-4" />
          <span className="hidden sm:inline">{viewLabel(currentView)}</span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-2">
          <User className="h-4 w-4" />
          {es ? 'Vistas Internas' : 'Internal Views'}
        </DropdownMenuLabel>
        {internalPersonas.map((view) => {
          const ViewIcon = view.icon;
          const isActive = !isPartnerView && persona === view.id;
          return (
            <DropdownMenuItem
              key={view.id}
              onClick={() => handleSelectPersona(view.id)}
              className={cn(
                'flex items-center gap-3 cursor-pointer',
                isActive && 'bg-accent'
              )}
            >
              <ViewIcon className="h-4 w-4 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium">{viewLabel(view)}</div>
                {view.description && (
                  <div className="text-xs text-muted-foreground">{viewDesc(view)}</div>
                )}
              </div>
              {isActive && <div className="w-2 h-2 rounded-full bg-primary" />}
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />

        <DropdownMenuLabel className="flex items-center gap-2">
          <Shield className="h-4 w-4" />
          {es ? 'Portales de Socios' : 'Partner Portals'}
          <Badge variant="outline" className="ml-auto text-[10px] px-1 py-0">DEMO</Badge>
        </DropdownMenuLabel>
        {partnerViews.map((view) => {
          const ViewIcon = view.icon;
          const isActive = currentPartnerView === view.id;
          return (
            <DropdownMenuItem
              key={view.id}
              onClick={() => handleSelectPartner(view)}
              disabled={view.disabled}
              className={cn(
                'flex items-center gap-3 cursor-pointer',
                isActive && 'bg-accent',
                view.disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <ViewIcon className="h-4 w-4 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium">{viewLabel(view)}</div>
                {view.description && (
                  <div className="text-xs text-muted-foreground">{viewDesc(view)}</div>
                )}
              </div>
              {isActive && <div className="w-2 h-2 rounded-full bg-primary" />}
              {view.disabled && (
                <Badge variant="outline" className="text-[10px] px-1 py-0">Soon</Badge>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ViewSwitcher;
